import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";
import moment from "moment";

// Utils
import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";

// Serializer
import DisChargeInterfaceSerializer from "issara-sdk/types/DisChargeInterfaceSerializer_apps_INF";

// API
import DisChargeInterfaceList from "issara-sdk/apis/DisChargeInterfaceList_apps_INF";
import DisChargeInterfaceUpdateView from "issara-sdk/apis/DisChargeInterfaceUpdateView_apps_INF";
import UserList from "issara-sdk/apis/UserList_users";

export type Props = {

}

export type State = Partial<{
  // seq
  TriggerDischargeInterfaceSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    dischargeInterfaceList: DisChargeInterfaceSerializer[];
    selectedItems: any[];
    userOptions: any[];
    searchDischarge: FilterSearchType;
    paginatedItems: PaginatedItemsType;
  }> | null;
}>;

export type FilterSearchType = Partial<{
  createdDate: string | null;
  hn: string | null;
  admitWard: string | null;
  dischargeWard: string | null;
  doctorId: number | string | null;
}>;

type PaginatedItemsType = Partial<{
  items: DisChargeInterfaceSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

type PickedState = Partial<
  Pick<MainState, "buttonLoadCheck" | "errorMessage" | "searchedItemListWithKey" | "successMessage">
>;

export type PickedProps = Partial<Omit<PickedState, "">>;

export type MasterOptionsType = Record<(typeof Masters)[number][0], OptionType[]>;

export type OptionType = {
  key: number | string;
  value: number | string;
  text: string;
};

// Sequence
type SeqState = {
  sequence: "TriggerDischargeInterface";
  restart?: boolean;
  clear?: boolean;
};

export type SaveType = "ADD" | "EDIT";

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

export type AccessorType = Partial<Record<keyof DisChargeInterfaceSerializer, string>>;

export type WidthsType = Partial<Record<keyof DisChargeInterfaceSerializer, number>>;

// Handle Action
type ActionType =
  | { action: "SEARCH"; cardKey: string; page?: number; pageSize?: number }
  | { action: "CLEAR"; cardKey: string }
  | { action: "SAVE"; cardKey: string; type: SaveType; selectedItems: any[] }; //* อาจจะส่ง params เพิ่ม

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  TriggerDischargeInterfaceSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  successMessage: null,
  errorMessage: null,
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
};

export const DataInitial = {};

const Masters = [] as const;

export const CARD_NAME = "CardTriggerDischargeInterface";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  SEARCH: "SEARCH",
  CLEAR: "CLEAR",
  SAVE: "SAVE",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const FILTER_INIT: FilterSearchType = {
  hn: "",
  admitWard: "",
  dischargeWard: "",
  doctorId: null,
};

const DOCTOR_SB_ID = `Doctor_${CARD_NAME}`;

export const LIST_LIMIT = 20;

const PAGINATED_INIT: PaginatedItemsType = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  const currentDate = dateToStringWithoutTimeBE(moment());

  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: Masters,
    },
  } as any);

  controller.setState(
    {
      TriggerDischargeInterfaceSequence: {
        ...state.TriggerDischargeInterfaceSequence,
        sequenceIndex: "Action",
        searchDischarge: {
          createdDate: currentDate,
        },
        paginatedItems: {
          pageSize: LIST_LIMIT,
          page: 0,
        },
      },
    },
    // () => Action(controller, { ...params, action: "SEARCH", cardKey: CARD_NAME })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.SEARCH]: HandleSearchData,
    [ACTIONS.CLEAR]: HandleClearData,
    [ACTIONS.SAVE]: HandleSaveData,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

const HandleSearchData: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const searchDischarge = state.TriggerDischargeInterfaceSequence?.searchDischarge;
  const doctorList = state.searchedItemListWithKey?.[`Doctor_${CARD_NAME}`] || [];
  const paginatedItems = state.TriggerDischargeInterfaceSequence?.paginatedItems;
  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: "LOADING",
    },
  });

  const offset = page * pageSize;

  const doctorCode = doctorList.find((item: any) => item.id === searchDischarge?.doctorId);

  let data = {
    limit: pageSize,
    offset,
    create_date: searchDischarge?.createdDate,
    hn: searchDischarge?.hn,
    admit_ward: searchDischarge?.admitWard,
    discharge_ward: searchDischarge?.dischargeWard,
    physicians: doctorCode?.code, // * issue 75325
  };

  const result = await GetDischargeInterfaceData(controller, data);

  if (result?.items) {
    const uniqueData = (result.items || [])
      .filter(
        (item: DisChargeInterfaceSerializer, index: number, self: any) =>
          index === self.findIndex((t: any) => t.created_by === item.created_by)
      )
      .map((item: DisChargeInterfaceSerializer) => item?.created_by);

    const userOptions = await Promise.all(
      uniqueData.map(async (id: number) => {
        const user = await GetUserList(controller, { id });

        return user;
      })
    );

    controller.setState({
      TriggerDischargeInterfaceSequence: {
        ...state.TriggerDischargeInterfaceSequence,
        dischargeInterfaceList: result?.items,
        userOptions: userOptions,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "SUCCESS",
      },
      searchedItemListWithKey: {
        ...state.searchedItemListWithKey,
        // [DOCTOR_SB_ID]: [],
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
    });
  }
};

const HandleSaveData: Handler<Params<"SAVE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${CARD_NAME}_${params.type}`]: "LOADING",
    },
  });

  const [saveResp, saveErr] = await SaveDischargeInterface(controller, {
    type: params.type,
    selectedItems: params.selectedItems,
  });

  if (saveResp) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: "The system has successfully",
      },
    });

    HandleSearchData(controller, { action: "SEARCH", cardKey: CARD_NAME });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: saveErr,
      },
    });
  }
};

const HandleClearData: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  const currentDate = dateToStringWithoutTimeBE(moment());

  controller.setState({
    TriggerDischargeInterfaceSequence: {
      ...state.TriggerDischargeInterfaceSequence,
      searchDischarge: {
        ...FILTER_INIT,
        createdDate: currentDate,
      },
      dischargeInterfaceList: [],
      paginatedItems: {
        ...PAGINATED_INIT,
      },
    },
    searchedItemListWithKey: {
      ...state.searchedItemListWithKey,
      [DOCTOR_SB_ID]: [],
    },
  });
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GetDischargeInterfaceData: Handler = async (controller, params) => {
  const state = controller.getState();

  /* params: 'create_date', 'hn', 'admit_ward', 'discharge_ward', 'physicians' */
  const [resp, err] = await DisChargeInterfaceList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const GetUserList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await UserList.list({
    apiToken: controller.apiToken,
    params: { pk: params.id },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp?.items?.[0];
};

const SaveDischargeInterface: Handler = async (controller, params) => {
  const state = controller.getState();

  const apiType =
    params.type === "ADD"
      ? DisChargeInterfaceUpdateView.post
      : DisChargeInterfaceUpdateView.put;

  const [resp, err] = await apiType({
    apiToken: controller.apiToken,
    data: {
      item_id_list: params?.selectedItems || [],
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

/**========================================================================
 **                               Utils
 *========================================================================**/

export const accessorList: Partial<Record<keyof DisChargeInterfaceSerializer, string>> = {
  id: "ID",
  valid: "Valid",
  error_message: "Error Message",
  created_by: "User ที่สร้าง",
  created_at: "วันเวลาที่สร้าง",
  sent: "Sent",
  file_name: "File Name",
  version: "Version",
  interface_date: "Interface Date",
  hn: "HN",
  encounter_type: "Encounter Type",
  encounter_id: "Encounter ID",
  weight: "Weight",
  admit_date: "Admit Date",
  admit_time: "Admit Time",
  discharge_date: "Discharge Date",
  discharge_time: "Discharge Time",
  admit_ward: "Admit Ward",
  admit_department: "Admit Department",
  discharge_ward: "Discharge Ward",
  discharge_department: "Discharge Department",
  physicians: "Physicians",
  discharge_step: "Discharge Step",
  discharge_stat: "Discharge Stat",
  discharge_type: "Discharge Type",
  insurance_code: "Insurance Code",
};

export const GenerateTableHeader = (
  columnnWidths?: WidthsType
): TableHeaderType<DisChargeInterfaceSerializer>[] => {
  return (Object.entries(accessorList) as [keyof DisChargeInterfaceSerializer, string][]).map(
    ([key, label]) => ({
      Header: label,
      accessor: key,
      ...(columnnWidths && columnnWidths[key] ? { width: columnnWidths[key] } : {}),
    })
  );
};